import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Link, Stack } from '@mui/material';
import { Home as HomeIcon, Email as EmailIcon, Phone as PhoneIcon, BugReport as BugReportIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import cwyLogo from '../../assets/cw-red-logo.svg';
import { ERROR_BOUNDARY_IMAGE } from './errorBoundaryImage';
import { isChunkLoadError } from '../../utils/lazyWithRetry';
import { captureException } from '../../config/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isChunkError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    isChunkError: false
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null,
      isChunkError: isChunkLoadError(error)
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isChunk = isChunkLoadError(error);
    
    this.setState({
      error,
      errorInfo,
      isChunkError: isChunk
    });

    // Log error to an error reporting service
    console.error('Uncaught error:', error, errorInfo);

    // Forward to Sentry with the React component stack so we can pinpoint
    // which screen/route threw (helps catch rare navigation render failures).
    captureException(error, {
      context: 'react_error_boundary',
      isChunkError: isChunk,
      componentStack: errorInfo?.componentStack,
      currentPath: window.location.pathname,
      href: window.location.href,
    });

    if (isChunk) {
      if (import.meta.env.DEV) {
        console.warn('[ErrorBoundary] Dynamic import failed in Vite dev — not treating as a new deploy');
      } else {
        console.warn('[ErrorBoundary] Chunk load error detected - new deployment may have occurred');
      }
    }
  }

  private handleGoHome = () => {
    window.location.href = '/app';
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleReportIssue = () => {
    const { error, errorInfo } = this.state;
    const errorMessage = error?.message || 'Unknown error';
    const errorStack = error?.stack || '';
    const componentStack = errorInfo?.componentStack || '';
    
    const subject = encodeURIComponent('Error Report - Application Error');
    const body = encodeURIComponent(
      `Hello Support Team,\n\n` +
      `I encountered an error while using the application.\n\n` +
      `Error Message: ${errorMessage}\n\n` +
      `Error Stack:\n${errorStack}\n\n` +
      `Component Stack:\n${componentStack}\n\n` +
      `Thank you for your assistance.`
    );
    
    window.location.href = `mailto:support@canadawaleyar.com?subject=${subject}&body=${body}`;
  };

  public render() {
    if (this.state.hasError) {
      // Skip in Vite dev — failed import() there is HMR/transform, not a shipped update.
      if (this.state.isChunkError && !import.meta.env.DEV) {
        return (
          <Box
            component="div"
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              bgcolor: '#f5f5f5',
              background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
              zIndex: 9999
            }}
          >
            <Box
              sx={{
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                p: 6,
                maxWidth: 500,
                width: '90%'
              }}
            >
              <Box sx={{ mb: 3 }}>
                <img 
                  src={cwyLogo} 
                  alt="Canadawale Yar Logo" 
                  style={{ maxWidth: '180px', height: 'auto' }} 
                />
              </Box>
              
              <RefreshIcon sx={{ fontSize: 64, color: '#757575', mb: 2 }} />
              
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#212121', mb: 2 }}>
                New Update Available!
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#616161', mb: 4, lineHeight: 1.6 }}>
                We've just deployed improvements to the application. 
                Please refresh the page to load the latest version.
              </Typography>
              
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRefresh}
                size="large"
                sx={{ 
                  bgcolor: '#f1f3f4',
                  color: '#0f0f0f',
                  px: 5,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#e8eaed' }
                }}
              >
                Refresh Page
              </Button>
              
              <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#9e9e9e' }}>
                Your work has been saved. No data will be lost.
              </Typography>
            </Box>
          </Box>
        );
      }
      
      // Regular error handling
      return (
        <Box
          component="div"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: { xs: 3, sm: 4, md: 6 },
            pb: 4,
            px: 3,
            textAlign: 'center',
            bgcolor: '#f5f5f5',
            background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
            overflow: 'auto',
            zIndex: 9999,
            margin: 0,
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              p: 6,
              maxWidth: 600,
              width: '100%'
            }}
          >
            {/* Logo */}
            <Box sx={{ mb: 4 }}>
              <img 
                src={cwyLogo} 
                alt="Canadawale Yar Logo" 
                style={{ 
                  maxWidth: '200px', 
                  height: 'auto',
                  marginBottom: '24px'
                }} 
              />
            </Box>

            {/* Error Image */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <img 
                src={ERROR_BOUNDARY_IMAGE} 
                alt="Error" 
                style={{ 
                  maxWidth: '250px', 
                  height: 'auto',
                  maxHeight: '200px'
                }} 
              />
            </Box>

            {/* Main Error Message */}
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: '#212121',
                mb: 2
              }}
            >
              Oops! Something went wrong.
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: 4,
                fontSize: '1.1rem',
                lineHeight: 1.6,
                color: '#616161'
              }}
            >
              We're sorry for the inconvenience. Please try refreshing the page, or press below button to go to home page. If problem persists, contact our support team.
            </Typography>

            {/* Action Buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 5 }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRefresh}
                size="large"
                sx={{ 
                  bgcolor: '#f1f3f4',
                  color: '#0f0f0f',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: '#e8eaed'
                  }
                }}
              >
                Refresh Page
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
                size="large"
                sx={{ 
                  borderColor: '#424242',
                  color: '#424242',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#212121',
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Go to Home page
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<BugReportIcon />}
                onClick={this.handleReportIssue}
                size="large"
                sx={{ 
                  borderColor: '#424242',
                  color: '#424242',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#212121',
                    bgcolor: '#f5f5f5'
                  }
                }}
              >
                Report Issue
              </Button>
            </Stack>

            {/* Support Contact Information */}
            <Box
              sx={{
                borderTop: '1px solid #e0e0e0',
                pt: 4,
                mt: 4
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 3,
                  color: '#757575',
                  fontWeight: 500
                }}
              >
                Need help? Contact our support team:
              </Typography>
              
              <Stack spacing={2} alignItems="center">
                <Link
                  href="mailto:support@canadawaleyar.com"
                  underline="none"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#424242',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&:hover': {
                      color: '#212121'
                    }
                  }}
                >
                  <EmailIcon sx={{ fontSize: '1.2rem' }} />
                  support@canadawaleyar.com
                </Link>
                
                <Link
                  href="tel:+16478298643"
                  underline="none"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#424242',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&:hover': {
                      color: '#212121'
                    }
                  }}
                >
                  <PhoneIcon sx={{ fontSize: '1.2rem' }} />
                  (647) 829-8643
                </Link>
              </Stack>
            </Box>
          </Box>

          {/* Development Error Details - Only show in development mode, not in production */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box 
              sx={{ 
                mt: 4, 
                textAlign: 'left', 
                width: '100%', 
                maxWidth: 900,
                bgcolor: 'white',
                borderRadius: 2,
                p: 3,
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#424242', mb: 2 }}>
                Error Details (Development Only):
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  border: '1px solid #e0e0e0',
                  color: '#212121',
                  maxHeight: '400px'
                }}
              >
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 